import api from "./axiosInstance";

export interface ProjectApprovalOverview {
  projectId: string;
  projectName: string;
  projectCode?: string;
  customer?: {
    _id: string;
    name: string;
  };
  location: {
    name?: string;
    address?: string;
    latitude?: string;
    longitude?: string;
  };
  timeline: {
    startDate?: string;
    endDate?: string;
    duration?: number;
  };
  totalAssets: number;
  totalEquipment: number;
  totalCheckpoints: number;
  assignedResources: {
    projectManager?: {
      _id: string;
      name: string;
      email: string;
    };
    projectHead?: {
      _id: string;
      name: string;
      email: string;
    };
    siteSupervisors: Array<{
      _id: string;
      name: string;
      email: string;
    }>;
    siteEngineers: Array<{
      _id: string;
      name: string;
      email: string;
    }>;
  };
  contractDocument?: string;
  /** All documents attached at project creation */
  attachedDocuments?: Array<{
    documentName: string;
    documentType: string;
    documentFile: string;
    uploadedAt?: string;
  }>;
  status: string;
  currentApprovalStatus?: string;
  resubmissionCount?: number;
  lastResubmittedAt?: string;
  resubmissionComments?: string; // Comments from the last resubmission
  requester: {
    _id: string;
    name: string;
    email: string;
  };
  // Project-Level Approval Status
  projectLevelApprovalStatus?: 'pending' | 'approved' | 'rejected';
  projectLevelApproverComments?: string;
  projectLevelApprovedAt?: Date;
  projectLevelRejectedAt?: Date;
  createdAt?: string;
  category?: string;
}

export interface CheckpointApprovalDetail {
  _id: string;
  name: string;
  frequency: string;
  requirement?: string;
  description?: string;
  taskType?: ('Visual' | 'Hearing' | 'Manual')[];
  isImageRequired?: boolean;
  isInputRequired?: string;
  executionPhase?: string;
  executionTime?: string;
  bufferTimeDays?: number;
  priority?: string;
  checkpointCode?: string;
  status: string;
  isProjectLevel: boolean;
  isMasterCopy: boolean;
  masterCheckpointId?: string;
  isModifiedFromMaster?: boolean;
  isNewlyAdded?: boolean;
  modifiedFields?: string[];
  originalValues?: Record<string, unknown>;
  lastModifiedAt?: string;
  lastModifiedById?: string;
  lastModifiedByName?: string;
  lastModifiedByEmail?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
  approverComments?: string;
  modificationType?: 'added' | 'modified' | 'deleted';
}

export interface EquipmentApprovalDetail {
  _id: string;
  equipmentCode: string;
  equipmentName: string;
  siteLabel?: string;
  makeAndModel?: string;
  checkpointCount: number;
  status: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
  approvalComments?: string;
}

export interface AssetApprovalDetail {
  _id: string;
  assetName: string;
  assetCode?: string;
  category?: string;
  type?: string;
  totalEquipment: number;
  totalCheckpoints: number;
  equipment: EquipmentApprovalDetail[];
  checkpoints: {
    projectLevelCheckpoints: CheckpointApprovalDetail[];
    masterCheckpoints: CheckpointApprovalDetail[];
  };
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
  approverComments?: string;
}

export interface ResourceWorkloadDetail {
  projectId: string;
  projectName: string;
  equipmentCount: number;
  role: string;
}

export interface ResourceApprovalDetail {
  _id: string;
  name: string;
  email: string;
  role: {
    name: string;
    roleType?: string;
  };
  allocatedEquipment: number;
  equipment?: Array<{
    _id: string;
    equipmentCode: string;
    equipmentName: string;
    siteLabel?: string;
    makeAndModel?: string;
    manufacturer?: string;
    serialNumber?: string;
    status: string;
    assetId?: string;
    assetName?: string;
  }>;
  subordinates?: ResourceApprovalDetail[];
  supervisor?: {
    _id: string;
    name: string;
    email: string;
  };
  otherProjects: ResourceWorkloadDetail[];
  assignmentId?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
  approverComments?: string;
  approvedAt?: Date;
  rejectedAt?: Date;
  approverId?: string; // Who approved/rejected this resource
  approverName?: string; // Approver's name
  approverEmail?: string; // Approver's email
}

export interface ReviewProgressSummary {
  totalItems: number;
  totalCheckpoints: number;
  totalResources: number;
  approved: number;
  rejected: number;
  pending: number;
  reviewed: number;
  percentage: number;
  checkpoints: {
    total: number;
    approved: number;
    rejected: number;
    pending: number;
    reviewed: number;
  };
  resources: {
    total: number;
    approved: number;
    rejected: number;
    pending: number;
    reviewed: number;
  };
}

export interface RejectedItemSummary {
  checkpoints: CheckpointApprovalDetail[];
  equipment: EquipmentApprovalDetail[];
  resources: ResourceApprovalDetail[];
}

export interface ProjectApprovalDetails {
  overview: ProjectApprovalOverview;
  assets: AssetApprovalDetail[];
  resources: {
    hierarchy: ResourceApprovalDetail[];
    independentEngineers: ResourceApprovalDetail[];
  };
  reviewProgress: ReviewProgressSummary;
  rejectedItems: RejectedItemSummary;
}

/** Approval history entry for timeline */
export interface ApprovalHistoryEntry {
  version: number;
  type: 'submitted' | 'resubmitted' | 'rejected' | 'approved';
  date: string;
  by?: string;
  reason?: string;
  notes?: string;
  summary?: { pending: number; approved: number; rejected: number; modified: number };
}

export interface ProjectApprovalHistory {
  totalSubmissions: number;
  resubmissionCount: number;
  history: ApprovalHistoryEntry[];
}

/**
 * Get approval history (submissions, rejections, approvals)
 */
export const getProjectApprovalHistory = async (
  projectId: string
): Promise<ProjectApprovalHistory> => {
  const response = await api.get(`/approvals/project/${projectId}/history`);
  return response.data.data;
};

/**
 * Get comprehensive project approval details
 */
export const getProjectApprovalDetails = async (
  projectId: string
): Promise<ProjectApprovalDetails> => {
  const response = await api.get(`/approvals/project/${projectId}`);
  return response.data.data;
};

/**
 * Get projects pending approval for current user
 */
export const getPendingProjectApprovals = async (): Promise<any[]> => {
  const response = await api.get('/approvals/pending?resourceType=project');
  return response.data.data || [];
};

/**
 * ============================================
 * INDIVIDUAL APPROVAL FUNCTIONS
 * ============================================
 * Individual approvals for checkpoints, equipment, assets
 * ============================================
 */

/**
 * Approve project asset
 */
export const approveProjectAsset = async (
  assetId: string,
  comments?: string
): Promise<void> => {
  await api.post(`/approvals/project/asset/${assetId}/approve`, { comments });
};

/**
 * Reject project asset
 */
export const rejectProjectAsset = async (
  assetId: string,
  comments: string
): Promise<void> => {
  await api.post(`/approvals/project/asset/${assetId}/reject`, { comments });
};

/**
 * Approve project checkpoint
 */
export const approveProjectCheckpoint = async (
  checkpointId: string,
  projectId: string,
  comments?: string
): Promise<void> => {
  await api.post(`/approvals/project/checkpoint/${checkpointId}/approve`, {
    projectId,
    comments
  });
};

/**
 * Reject project checkpoint
 */
export const rejectProjectCheckpoint = async (
  checkpointId: string,
  projectId: string,
  comments: string
): Promise<void> => {
  await api.post(`/approvals/project/checkpoint/${checkpointId}/reject`, {
    projectId,
    comments
  });
};

/**
 * Approve equipment
 */
export const approveEquipment = async (
  equipmentId: string,
  comments?: string
): Promise<void> => {
  await api.post(`/approvals/project/equipment/${equipmentId}/approve`, { comments });
};

/**
 * Reject equipment
 */
export const rejectEquipment = async (
  equipmentId: string,
  comments: string
): Promise<void> => {
  await api.post(`/approvals/project/equipment/${equipmentId}/reject`, { comments });
};

/**
 * Approve all master checkpoints for an asset
 */
export const approveMasterCheckpointsForAsset = async (
  assetId: string,
  comments?: string
): Promise<{ approvedCount: number }> => {
  const response = await api.post(`/approvals/project/asset/${assetId}/checkpoints/master/approve`, { comments });
  return response.data.data;
};

/**
 * Reject all master checkpoints for an asset
 */
export const rejectMasterCheckpointsForAsset = async (
  assetId: string,
  comments: string
): Promise<{ rejectedCount: number }> => {
  const response = await api.post(`/approvals/project/asset/${assetId}/checkpoints/master/reject`, { comments });
  return response.data.data;
};

/**
 * ============================================
 * RESOURCE ASSIGNMENT APPROVAL FUNCTIONS
 * ============================================
 */

/**
 * Approve resource assignment - Simple: just projectId and resourceId
 */
export const approveResourceAssignment = async (
  projectId: string,
  resourceId: string,
  comments?: string
): Promise<void> => {
  await api.post(`/approvals/project/${projectId}/resource/${resourceId}/approve`, { comments });
};

/**
 * Reject resource assignment - Simple: just projectId and resourceId
 */
export const rejectResourceAssignment = async (
  projectId: string,
  resourceId: string,
  comments: string
): Promise<void> => {
  await api.post(`/approvals/project/${projectId}/resource/${resourceId}/reject`, { comments });
};

/**
 * ============================================
 * PROJECT-LEVEL APPROVAL FUNCTIONS
 * ============================================
 */

/**
 * Approve project-level (overview/details)
 */
export const approveProjectLevel = async (
  projectId: string,
  comments?: string
): Promise<void> => {
  await api.post(`/approvals/project/${projectId}/approve`, { comments });
};

/**
 * Reject project-level (overview/details)
 */
export const rejectProjectLevel = async (
  projectId: string,
  comments: string
): Promise<void> => {
  await api.post(`/approvals/project/${projectId}/reject`, { comments });
};

/**
 * Approve All & Submit - Head user approves everything and finalizes project
 * This approves all checkpoints, equipment, assets, resources, and project-level,
 * sets project to approved/active, generates tasks, and locks the project
 */
export const approveAllAndSubmit = async (
  projectId: string,
  comments?: string
): Promise<{ tasksGenerated: number }> => {
  const response = await api.post(`/approvals/project/${projectId}/approve-all-and-submit`, { comments });
  return response.data.data;
};

/**
 * ============================================
 * BULK CHECKPOINT OPERATIONS (Master Checkpoints Only)
 * ============================================
 */

/**
 * Approve multiple checkpoints at once (for master checkpoints)
 */
export const approveBulkCheckpoints = async (
  checkpointIds: string[],
  projectId: string,
  comments?: string
): Promise<{ approvedCount: number }> => {
  const response = await api.post('/approvals/project/checkpoints/bulk-approve', {
    checkpointIds,
    projectId,
    comments
  });
  return response.data.data;
};

/**
 * Reject multiple checkpoints with single comment (for master checkpoints)
 */
export const rejectBulkCheckpoints = async (
  checkpointIds: string[],
  projectId: string,
  comments: string
): Promise<{ rejectedCount: number }> => {
  const response = await api.post('/approvals/project/checkpoints/bulk-reject', {
    checkpointIds,
    projectId,
    comments
  });
  return response.data.data;
};


/**
 * Resubmit project for approval
 */
export const resubmitProject = async (
  projectId: string,
  comments?: string
): Promise<void> => {
  await api.post(`/approvals/project/${projectId}/resubmit`, { comments });
};

export default {
  getProjectApprovalDetails,
  getProjectApprovalHistory,
  getPendingProjectApprovals,
  approveProjectAsset,
  rejectProjectAsset,
  approveProjectCheckpoint,
  rejectProjectCheckpoint,
  approveEquipment,
  rejectEquipment,
  approveMasterCheckpointsForAsset,
  rejectMasterCheckpointsForAsset,
  approveResourceAssignment,
  rejectResourceAssignment,
  approveProjectLevel,
  rejectProjectLevel,
  approveAllAndSubmit,
  approveBulkCheckpoints,
  rejectBulkCheckpoints,
  resubmitProject
};
